import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGetSuivisForCoach, apiGetSportive } from '../services/api';
import PageHeader from '../components/PageHeader';
import useTeams from '../hooks/useTeams';
import { COACH, MENTAL, SPORT } from '../constants/colors';
import './CoachTeams.css';

const PRESET_COLORS = COACH.teamPalette;
const AVATAR_COLORS = [MENTAL.accent, MENTAL.estime, MENTAL.concentration, SPORT.rpeFacile, SPORT.primary];

const CoachTeams = () => {
  const { user, getToken } = useAuth();
  const token = getToken();

  const {
    teams, error: teamsError,
    createTeam, deleteTeam, addMember, removeMember,
  } = useTeams(user?.id, token);

  // Seuls le suivi et le profil sont chargés ici : composer une équipe ne demande
  // que des noms. Les relevés de santé restent sur le tableau de bord, où le
  // partage les conditionne.
  const [suivis, setSuivis] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);

  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const loadAthletes = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    try {
      const suiviList = await apiGetSuivisForCoach(user.id, token);
      setSuivis(suiviList);
      const profileMap = {};
      await Promise.all(suiviList.map(async (s) => {
        const profile = await apiGetSportive(s.id_sportive, token).catch(() => null);
        if (profile) profileMap[s.id_sportive] = profile;
      }));
      setProfiles(profileMap);
    } finally {
      setLoading(false);
    }
  }, [user?.id, token]);

  useEffect(() => { loadAthletes(); }, [loadAthletes]);

  const selectedTeam = teams.find((t) => t.id === selectedTeamId) || null;

  const handleCreate = () => {
    if (!newName.trim()) return;
    createTeam(newName.trim(), newColor);
    setNewName('');
    setNewColor(PRESET_COLORS[0]);
  };

  // ── Vue B : composition d'une équipe ──────────────────────────────────────
  if (selectedTeam) {
    return (
      <div className="coach-teams">
        <PageHeader title="Équipes" />

        {teamsError && <p className="coach-teams__error">{teamsError}</p>}

        <div className="coach-teams__section">
          <div className="coach-teams__member-head">
            <button
              className="coach__team-back-btn"
              onClick={() => setSelectedTeamId(null)}
              aria-label="Retour aux équipes"
            >
              ‹
            </button>
            <div className="coach__team-modal-team-name">
              <span className="coach__team-modal-dot" style={{ background: selectedTeam.color }} />
              {selectedTeam.name}
            </div>
          </div>

          <div className="coach__team-member-list">
            {suivis.length === 0 ? (
              <p className="coach__team-member-list-empty">Aucune sportive suivie.</p>
            ) : (
              suivis.map((s, idx) => {
                const sid = s.id_sportive;
                const p = profiles[sid];
                const fullName = p ? `${p.prenom} ${p.nom}` : `Sportive #${sid}`;
                const initials = p ? `${p.prenom[0]}${p.nom[0]}` : `S${idx + 1}`;
                const avatarBg = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                const isMember = selectedTeam.memberIds.includes(sid);
                const toggle = () => (isMember
                  ? removeMember(selectedTeam.id, sid)
                  : addMember(selectedTeam.id, sid));

                return (
                  <div key={sid} className="coach__team-member-row" onClick={toggle}>
                    <div className="coach__team-member-avatar" style={{ background: avatarBg }}>
                      {initials}
                    </div>
                    <div className="coach__team-member-info">
                      <span className="coach__team-member-name">{fullName}</span>
                      {p?.sport && <span className="coach__team-member-sub">{p.sport}</span>}
                    </div>
                    <div className={`coach__team-member-check${isMember ? ' coach__team-member-check--on' : ''}`}>
                      {isMember && '✓'}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Vue A : liste des équipes ─────────────────────────────────────────────
  return (
    <div className="coach-teams">
      <PageHeader title="Équipes" />

      {teamsError && <p className="coach-teams__error">{teamsError}</p>}

      <div className="coach-teams__section">
        <div className="coach__team-create">
          <p className="coach__team-create-label">Nouvelle équipe</p>
          <div className="coach__team-swatches">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                className={`coach__team-swatch${newColor === c ? ' coach__team-swatch--selected' : ''}`}
                style={{ background: c }}
                onClick={() => setNewColor(c)}
                aria-label={`Couleur ${c}`}
              />
            ))}
          </div>
          <div className="coach__team-create-row">
            <input
              className="coach-teams__input"
              type="text"
              placeholder="Nom de l'équipe…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              maxLength={64}
            />
            <button
              className="coach__team-create-btn"
              onClick={handleCreate}
              disabled={!newName.trim()}
            >
              Créer
            </button>
          </div>
        </div>
      </div>

      <div className="coach-teams__section">
        <h2 className="coach-teams__section-title">Mes équipes</h2>
        <div className="coach__team-list">
          {teams.length === 0 ? (
            <p className="coach__team-list-empty">
              Aucune équipe. Crée-en une ci-dessus pour filtrer ton tableau de bord.
            </p>
          ) : (
            teams.map((team) => (
              <div key={team.id} className="coach__team-row">
                {confirmDeleteId === team.id ? (
                  <div className="coach__team-confirm">
                    <span className="coach__team-confirm-text">
                      Supprimer «&nbsp;{team.name}&nbsp;» ?
                    </span>
                    <button
                      className="coach__team-confirm-cancel"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      Annuler
                    </button>
                    <button
                      className="coach__team-confirm-delete"
                      onClick={() => { deleteTeam(team.id); setConfirmDeleteId(null); }}
                    >
                      Supprimer
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="coach__team-dot" style={{ background: team.color }} />
                    <span className="coach__team-row-name">{team.name}</span>
                    <span className="coach__team-count">
                      {team.memberIds.length} membre{team.memberIds.length !== 1 ? 's' : ''}
                    </span>
                    <button
                      className="coach__team-row-btn"
                      onClick={() => { setSelectedTeamId(team.id); setConfirmDeleteId(null); }}
                    >
                      Membres
                    </button>
                    <button
                      className="coach__team-delete-btn"
                      onClick={() => setConfirmDeleteId(team.id)}
                      aria-label={`Supprimer ${team.name}`}
                    >
                      🗑
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
        {loading && <p className="coach__team-list-empty">Chargement des sportives…</p>}
      </div>
    </div>
  );
};

export default CoachTeams;
